
<script setup>
import { computed, ref } from "vue";
import { useRouter } from "vue-router";
import MainLayout from "@/components/layout/MainLayout.vue";
import PageHeader from "@/components/common/PageHeader.vue";
import UnitStatCard from "@/components/units/UnitStatCard.vue";
import UnitFilterBar from "@/components/units/UnitFilterBar.vue";
import UnitTable from "@/components/units/UnitTable.vue";
import UnitFormModal from "@/components/units/UnitFormModal.vue";
import { useUnitStore } from "@/stores/unit";

const router = useRouter();
const unitStore = useUnitStore();

const unitFormModal = ref(null);

const search = computed({
  get: () => unitStore.filters.search,
  set: (v) => unitStore.setSearch(v),
});
const unitType = computed({
  get: () => unitStore.filters.unitType,
  set: (v) => unitStore.setUnitType(v),
});
const paymentStatus = computed({
  get: () => unitStore.filters.paymentStatus,
  set: (v) => unitStore.setPaymentStatus(v),
});

const formatCurrency = (v) =>
  new Intl.NumberFormat("th-TH", {
    style: "currency",
    currency: "THB",
    minimumFractionDigits: 0,
  }).format(v || 0);
</script>
<template>
  <MainLayout>
    <div class="container-fluid px-0">
      <PageHeader
        title="รายการยูนิต / บ้าน / ห้อง"
        subtitle="จัดการข้อมูลยูนิต ลูกบ้าน สถานะการชำระ และยอดค่าส่วนกลาง"
        button-text="เพิ่มยูนิต"
        button-icon="bi-plus-circle"
        button-color="info"
        @action="unitFormModal.open()"
      />

      <div class="row g-4 mb-4">
        <div class="col-md-6 col-xl-3">
          <UnitStatCard
            title="ยูนิตทั้งหมด"
            :value="unitStore.totalUnits.toLocaleString('th-TH')"
            subtitle="จำนวนยูนิตทั้งหมดในระบบ"
            icon="bi-buildings"
            color="primary"
          />
        </div>
        <div class="col-md-6 col-xl-3">
          <UnitStatCard
            title="มีผู้พักอาศัย"
            :value="unitStore.occupiedUnits.toLocaleString('th-TH')"
            subtitle="ยูนิตที่มีการใช้งานอยู่"
            icon="bi-house-check"
            color="success"
          />
        </div>
        <div class="col-md-6 col-xl-3">
          <UnitStatCard
            title="ค้างชำระ"
            :value="unitStore.overdueUnits.toLocaleString('th-TH')"
            subtitle="ยูนิตที่ยังไม่ชำระครบ"
            icon="bi-exclamation-circle"
            color="danger"
          />
        </div>
        <div class="col-md-6 col-xl-3">
          <UnitStatCard
            title="ยอดค้างรวม"
            :value="formatCurrency(unitStore.totalOutstanding)"
            subtitle="รวมยอดค้างชำระทั้งหมด"
            icon="bi-cash-stack"
            color="warning"
          />
        </div>
      </div>

      <UnitFilterBar
        :search="search"
        :unit-type="unitType"
        :payment-status="paymentStatus"
        :unit-type-options="unitStore.unitTypeOptions"
        :payment-status-options="unitStore.paymentStatusOptions"
        @update:search="search = $event"
        @update:unitType="unitType = $event"
        @update:paymentStatus="paymentStatus = $event"
        @reset="unitStore.resetFilters()"
      />

      <div class="row g-4 mb-4">
        <div class="col-12">
          <div
            class="card border-0 shadow-sm rounded-4 bg-primary bg-opacity-10"
          >
            <div class="card-body p-4">
              <div
                class="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3"
              >
                <div>
                  <div class="fw-bold fs-5 mb-1">
                    รายได้ค่าส่วนกลางที่คาดว่าจะจัดเก็บต่อเดือน
                  </div>
                  <div class="text-muted">
                    ประมาณการจากอัตราค่าส่วนกลางของทุกยูนิตในระบบ
                  </div>
                </div>
                <div class="text-lg-end">
                  <div class="display-6 fw-bold text-primary mb-0">
                    {{ formatCurrency(unitStore.expectedMonthlyIncome) }}
                  </div>
                  <div class="text-muted small">ต่อเดือน</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <UnitTable
        :items="unitStore.filteredUnits"
        @view="(u) => router.push(`/units/${u.id}`)"
        @edit="(u) => unitFormModal.open(u)"
      />
    </div>

    <UnitFormModal ref="unitFormModal" />
  </MainLayout>
</template>